import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { identifyUser, resetAnalytics } from '@/lib/analytics';
import {
  getPatientSession,
  PATIENT_PORTAL_QUERY_PREFIX,
  patientSignOut,
  type PatientSession,
} from '@/lib/patientPortalApi';

export type PatientSessionState =
  | { status: 'loading' }
  /** No session the API will refresh: the patient signs in. */
  | { status: 'none' }
  /** A staff session: the patient pages refuse it rather than 403 on every call. */
  | { status: 'staff' }
  | { status: 'error' }
  | { status: 'patient'; session: PatientSession };

interface PatientAuthContextType {
  state: PatientSessionState;
  reload: () => void;
  signOut: () => Promise<void>;
  /** Set when the API did not end the session: the cookies still work. */
  signOutError: string | null;
}

export const SIGN_OUT_FAILED = 'We could not sign you out. Try again.';

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(
  undefined
);

/**
 * The patient counterpart of AuthProvider. It asks only `/auth/me`: the staff
 * provider follows up with clinic and invitation routes that 403 for a patient.
 * Identified by user id alone; name and email never reach analytics.
 */
export function PatientAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PatientSessionState>({
    status: 'loading',
  });
  const [attempt, setAttempt] = useState(0);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getPatientSession()
      .then(session => {
        if (cancelled) return;
        if (!session) {
          setState({ status: 'none' });
          return;
        }
        if (session.user.kind !== 'PATIENT') {
          setState({ status: 'staff' });
          return;
        }
        identifyUser(session.user.id);
        setState({ status: 'patient', session });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt(n => n + 1), []);

  // Only the API's answer ends a session: a failed request leaves the cookies
  // valid, so the page stays signed in and says so.
  const signOut = useCallback(async () => {
    setSignOutError(null);
    try {
      await patientSignOut();
    } catch {
      setSignOutError(SIGN_OUT_FAILED);
      return;
    }
    // The next patient on this browser must not see this one's pages.
    queryClient.removeQueries({ queryKey: PATIENT_PORTAL_QUERY_PREFIX });
    resetAnalytics();
    setState({ status: 'none' });
  }, [queryClient]);

  const value = useMemo(
    () => ({ state, reload, signOut, signOutError }),
    [state, reload, signOut, signOutError]
  );

  return (
    <PatientAuthContext.Provider value={value}>
      {children}
    </PatientAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePatientAuth(): PatientAuthContextType {
  const context = useContext(PatientAuthContext);
  if (!context) {
    throw new Error('usePatientAuth must be used within a PatientAuthProvider');
  }
  return context;
}

/** For pages under PatientSessionScope, which renders them only with a patient session. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePatientSession(): PatientSession {
  const { state } = usePatientAuth();
  if (state.status !== 'patient') {
    throw new Error('usePatientSession must be used under PatientSessionScope');
  }
  return state.session;
}
